// /settings → "COD тохиргоо" tab (spec §2.7a item 7).
// Backed by the same governance-config endpoint — only the codConfig
// JSON sub-object is touched here.
// Nested tabs: "Тохиргоо" (config form) + "Тооцоо" (reconciliation worklist).

import {
  Checkbox,
  Group,
  NumberInput,
  Skeleton,
  Stack,
  Switch,
  Tabs,
  Text,
} from "@mantine/core"
import { HelpIcon } from "@dehub/theme"
import { useForm } from "@mantine/form"
import { CurrencyDollarIcon, GearSixIcon } from "@phosphor-icons/react"
import { useEffect } from "react"
import { useTranslation } from "react-i18next"
import type { IGovernanceConfig } from "../../apis/governance-config"
import { useGovernanceConfigQuery, useSettingsMutations } from "./hooks"
import { ListState } from "../ui"
import { SETTINGS_WRITE, SettingsButton, SettingsFields } from "./settings-controls"
import { CodReconciliationWorklist } from "./CodReconciliationWorklist"

function CodConfigForm() {
  const { t } = useTranslation("settings")
  const { data, isLoading, error, mutate: reload } = useGovernanceConfigQuery()
  const mutations = useSettingsMutations()

  const form = useForm({
    initialValues: {
      enabled: false,
      maxAmountPerDn: null as number | null,
      paymentMethods: [] as string[],
      requireCodReceiptPhoto: true,
      maxDailyCodPerDriver: null as number | null,
    },
  })

  useEffect(() => {
    if (!data) return
    form.setValues({
      enabled: data.codConfig?.enabled ?? false,
      maxAmountPerDn: data.codConfig?.maxAmountPerDn ?? null,
      paymentMethods: data.codConfig?.paymentMethods ?? ["CASH"],
      requireCodReceiptPhoto: data.codConfig?.requireCodReceiptPhoto ?? true,
      maxDailyCodPerDriver: data.codConfig?.maxDailyCodPerDriver ?? null,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  if (isLoading) return <Skeleton height={120} radius="md" />
  if (error) {
    return (
      <ListState state="error" title={t("cod.config.errorTitle")} description={t("cod.config.loadError")} onRetry={() => reload()} />
    )
  }

  return (
    <Stack gap="md" maw={520}>
      <SettingsFields code={SETTINGS_WRITE.governance}>
        <Switch
          label={t("cod.config.fields.enabled.label")}
          description={t("cod.config.fields.enabled.description")}
          checked={form.values.enabled}
          onChange={(e) => form.setFieldValue("enabled", e.currentTarget.checked)}
        />

        <NumberInput
          label={<>{t("cod.config.fields.maxAmountPerDn.label")}<HelpIcon helpKey="order.settings.field.cod-max-per-dn" /></>}
          description={t("cod.config.fields.maxAmountPerDn.description")}
          min={0}
          value={form.values.maxAmountPerDn ?? ""}
          onChange={(v) =>
            form.setFieldValue("maxAmountPerDn", v === "" ? null : Number(v))
          }
          thousandSeparator=","
        />

        <Checkbox.Group
          label={t("cod.config.fields.paymentMethods.label")}
          description={t("cod.config.fields.paymentMethods.description")}
          value={form.values.paymentMethods ?? []}
          onChange={(value) => form.setFieldValue("paymentMethods", value)}
        >
          <Group mt="xs">
            <Checkbox value="CASH" label={t("cod.config.options.paymentMethod.CASH")} />
            <Checkbox value="CARD" label={t("cod.config.options.paymentMethod.CARD")} />
            <Checkbox value="QPAY" label={t("cod.config.options.paymentMethod.QPAY")} />
          </Group>
        </Checkbox.Group>

        <Switch
          label={t("cod.config.fields.requireCodReceiptPhoto.label")}
          description={t("cod.config.fields.requireCodReceiptPhoto.description")}
          checked={form.values.requireCodReceiptPhoto}
          onChange={(e) =>
            form.setFieldValue("requireCodReceiptPhoto", e.currentTarget.checked)
          }
        />

        <NumberInput
          label={<>{t("cod.config.fields.maxDailyCodPerDriver.label")}<HelpIcon helpKey="order.settings.field.cod-max-daily" /></>}
          description={t("cod.config.fields.maxDailyCodPerDriver.description")}
          min={0}
          value={form.values.maxDailyCodPerDriver ?? ""}
          onChange={(v) =>
            form.setFieldValue(
              "maxDailyCodPerDriver",
              v === "" ? null : Number(v)
            )
          }
          thousandSeparator=","
        />
      </SettingsFields>

      <Group justify="flex-end">
        <SettingsButton
          code={SETTINGS_WRITE.governance}
          helpKey="order.settings.action.save-cod"
          loading={mutations.submitting}
          onClick={() =>
            mutations.governance.update({
              codConfig: {
                ...((data as IGovernanceConfig).codConfig ?? { paymentMethods: [] }),
                enabled: form.values.enabled,
                maxAmountPerDn: form.values.maxAmountPerDn,
                paymentMethods: form.values.paymentMethods,
                requireCodReceiptPhoto: form.values.requireCodReceiptPhoto,
                maxDailyCodPerDriver: form.values.maxDailyCodPerDriver,
              },
            })
          }
        >
          {t("common.save")}
        </SettingsButton>
      </Group>
    </Stack>
  )
}

export function CodConfigTab() {
  const { t } = useTranslation("settings")

  return (
    <Tabs defaultValue="config" keepMounted={false}>
      <Tabs.List>
        <Tabs.Tab value="config" leftSection={<GearSixIcon size={16} />}>
          {t("cod.tabs.config")}
        </Tabs.Tab>
        <Tabs.Tab value="reconciliation" leftSection={<CurrencyDollarIcon size={16} />}>
          {t("cod.tabs.reconciliation")}
        </Tabs.Tab>
      </Tabs.List>

      <Tabs.Panel value="config" pt="md">
        <CodConfigForm />
      </Tabs.Panel>

      <Tabs.Panel value="reconciliation" pt="md">
        <Text fw={600} fz="sm" mb="sm">
          {t("cod.reconciliation.title")}
          <HelpIcon helpKey="order.settings.section.cod-reconciliation" variant="section" />
        </Text>
        <CodReconciliationWorklist />
      </Tabs.Panel>
    </Tabs>
  )
}
